import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, XCircle, Loader, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import './LiveDemo.css';

// const API_BASE_URL = 'http://94.130.150.202:3100';
const API_BASE_URL = '/api';
const API_KEY = 'baf9d30126e8d595669777c9ccdbc64debaac8056df7cc1f3f91e843674a205c';
const DEMO_ID_STORAGE_KEY = 'fastr_demo_id';
const DEMO_EXPIRES_STORAGE_KEY = 'fastr_demo_expires';

const SETUP_MESSAGES = [
  'Setting up the fabric network',
  'Starting neo4j',
  'Deploying servers'
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function readStorage() {
  const id = sessionStorage.getItem(DEMO_ID_STORAGE_KEY);
  const expiresAt = sessionStorage.getItem(DEMO_EXPIRES_STORAGE_KEY);
  const valid =
    id &&
    expiresAt &&
    new Date().getTime() < new Date(expiresAt).getTime();
  return { id, expiresAt, valid: !!valid, hasId: !!id };
}

function clearStorage() {
  sessionStorage.removeItem(DEMO_ID_STORAGE_KEY);
  sessionStorage.removeItem(DEMO_EXPIRES_STORAGE_KEY);
}

// ── Component ─────────────────────────────────────────────────────────────────

function LiveDemo({ onDemoReady, onStatusChange }) {
  const isMobile = window.innerWidth <= 768;

  const [isOpen, setIsOpen] = useState(false);
  const [demoState, setDemoState] = useState('closed');
  const [healthData, setHealthData] = useState(null);
  const [error, setError] = useState(null);
  const [demoId, setDemoId] = useState(() => sessionStorage.getItem(DEMO_ID_STORAGE_KEY) || null);
  const [expiresAt, setExpiresAt] = useState(() => sessionStorage.getItem(DEMO_EXPIRES_STORAGE_KEY) || null);
  const [progress, setProgress] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [setupMessageIndex, setSetupMessageIndex] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [userEmail, setUserEmail] = useState(null);

  const pollingIntervalRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const messageIntervalRef = useRef(null);
  const proxyPollingIntervalRef = useRef(null);

  // ── Mount / cleanup ─────────────────────────────────────────────────────────
  // Re-derives the real phase from sessionStorage and resumes polling automatically,
  // so a background launch survives closing the dialog, navigating away, or a refresh
  // without needing another click to "reconnect" to it.

  useEffect(() => {
    const { id, expiresAt: storedExpiry, valid, hasId } = readStorage();

    if (hasId && !storedExpiry) {
      // Phase 1 (AWS spot instance) was still in flight last we knew — resume polling.
      resumeDemo(id);
    } else if (hasId && storedExpiry && valid) {
      // AWS instance is up, but we never persisted whether the app itself was
      // confirmed reachable — re-check rather than assuming it still is.
      setStatusMessage('Instance acquired, setting up the demo');
      setDemoState('polling-ready');
      startProgressBar();
      startSetupMessages();
      startPollingProxy(id);
    } else if (hasId && storedExpiry && !valid) {
      clearStorage();
      setDemoId(null);
      setExpiresAt(null);
    }

    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      if (messageIntervalRef.current) clearInterval(messageIntervalRef.current);
      if (proxyPollingIntervalRef.current) clearInterval(proxyPollingIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Report status upstream ──────────────────────────────────────────────────
  // Lets the page render a shared "what's happening right now" panel outside this
  // component, since this component itself no longer owns a visible status dialog
  // once a demo is actually launching.

  useEffect(() => {
    if (!onStatusChange) return;

    if (demoState === 'polling-ip') {
      onStatusChange({ phase: 'requesting-instance', message: statusMessage });
    } else if (demoState === 'polling-ready') {
      onStatusChange({ phase: 'provisioning', message: SETUP_MESSAGES[setupMessageIndex] });
    } else if (demoState === 'complete') {
      onStatusChange({ phase: 'ready', message: 'Your demo is live and ready to explore.' });
    } else if (demoState === 'error' && !isOpen) {
      onStatusChange({ phase: 'error', message: error || 'Demo unavailable' });
    } else {
      onStatusChange({ phase: 'idle' });
    }
  }, [demoState, statusMessage, setupMessageIndex, error, isOpen, onStatusChange]);

  // ── API helper ──────────────────────────────────────────────────────────────

  const apiRequest = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        'X-API-Key': API_KEY,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`API request failed: ${response.status}`);
    }

    return response;
  };

  // ── Code verification (disabled — replaced by email capture below) ─────────

  // const calculateDailyCode = () => {
  //   const today = new Date();
  //   const day = today.getDate();
  //   const month = today.getMonth() + 1;
  //   const year = today.getFullYear();
  //   return (day * month) + year;
  // };

  // const verifyCode = () => {
  //   const expectedCode = calculateDailyCode();
  //   if (parseInt(codeInput) === expectedCode) {
  //     setCodeError('');
  //     checkHealth();
  //   } else {
  //     setCodeError('Incorrect code');
  //   }
  // };

  // ── Email capture ────────────────────────────────────────────────────────────

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const submitEmail = () => {
    const trimmed = emailInput.trim();
    if (!isValidEmail(trimmed)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    setUserEmail(trimmed);
    checkHealth();
  };

  const skipEmail = () => {
    setEmailError('');
    setUserEmail(null);
    checkHealth();
  };

  // ── Health check ────────────────────────────────────────────────────────────

  const checkHealth = async () => {
    setDemoState('checking');
    setError(null);

    try {
      const response = await apiRequest(`${API_BASE_URL}/health`);
      const data = await response.json();

      if (data.status === 'ok' && data.database === 'connected') {
        setHealthData(data);
        setDemoState('ready');
      } else {
        setError('Demo service is not ready');
        setDemoState('error');
      }
    } catch (err) {
      setError('Demo unavailable');
      setDemoState('error');
    }
  };

  // ── Resume a launching demo ─────────────────────────────────────────────────

  const resumeDemo = (idToResume) => {
    setStatusMessage('Requesting amazon for a spot instance');
    setDemoState('polling-ip');
    startPollingForIp(idToResume);
  };

  // ── Launch a new demo ───────────────────────────────────────────────────────

  const launchDemo = async () => {
    setDemoState('launching');
    setError(null);
    setProgress(0);
    setElapsedTime(0);
    setStatusMessage('Requesting amazon for a spot instance');

    try {
      const response = await apiRequest(`${API_BASE_URL}/api/demos`, {
        method: 'POST',
        body: JSON.stringify({ userEmail: userEmail || 'demo@fastrv2.com' }),
      });

      const data = await response.json();

      if (data.demoId) {
        setDemoId(data.demoId);
        sessionStorage.setItem(DEMO_ID_STORAGE_KEY, data.demoId);

        setDemoState('polling-ip');
        setIsOpen(false);
        startPollingForIp(data.demoId);
      } else {
        setError('Failed to create demo');
        setDemoState('error');
      }
    } catch (err) {
      if (err.message && err.message.includes('429')) {
        setError('Every demo launched costs me money! If you have further questions about this work, please contact me on - suraj.gupta@euris.com');
      } else {
        setError('Demo unavailable');
      }
      setDemoState('error');
    }
  };

  // ── Phase 1: poll until status = 'running' ──────────────────────────────────

  const startPollingForIp = (demoIdToCheck) => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await apiRequest(`${API_BASE_URL}/api/demos/${demoIdToCheck}`);
        const data = await response.json();

        if (data.status && !['launching', 'running'].includes(data.status)) {
          clearInterval(pollingIntervalRef.current);
          setError('AWS spot request timed out. Please try again later.');
          setDemoState('error');
          clearStorage();
          setDemoId(null);
          setExpiresAt(null);
          return;
        }

        if (data.status === 'running') {
          if (data.expiresAt) {
            setExpiresAt(data.expiresAt);
            sessionStorage.setItem(DEMO_EXPIRES_STORAGE_KEY, data.expiresAt);
          }
          setStatusMessage('Instance acquired, setting up the demo');
          setDemoState('polling-ready');
          clearInterval(pollingIntervalRef.current);

          startProgressBar();
          startSetupMessages();
          startPollingProxy(demoIdToCheck);
        }
      } catch (err) {
        console.error('Error polling demo status:', err);
      }
    }, 5000);
  };

  // ── Phase 2: poll until both the frontend and the EHR backend are serving ──
  // The frontend (port 3002) is a static server and boots fast, so checking it
  // alone isn't a reliable readiness signal — it can respond well before Neo4j
  // and the Fabric blockchain clients (the real bottleneck) finish connecting.
  // ehr-client-server only binds its port once those are connected, so its
  // /health being reachable at all is a much stronger guarantee.

  const startPollingProxy = (demoIdToCheck) => {
    proxyPollingIntervalRef.current = setInterval(async () => {
      try {
        const [frontendResponse, backendResponse] = await Promise.all([
          fetch(`/demo-proxy/${demoIdToCheck}`),
          fetch(`/demo-ehr/${demoIdToCheck}/health`),
        ]);

        const frontendReady = frontendResponse.status === 200 || frontendResponse.status === 404;
        const backendReady = backendResponse.status === 200;

        if (frontendReady && backendReady) {
          setProgress(100);
          setDemoState('complete');

          clearInterval(proxyPollingIntervalRef.current);
          if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
          if (messageIntervalRef.current) clearInterval(messageIntervalRef.current);
        }
      } catch (err) {
        // Network error — keep polling
      }
    }, 5000);
  };

  // ── Progress bar ────────────────────────────────────────────────────────────

  const startProgressBar = () => {
    const targetTime = 120000;
    const updateInterval = 100;
    let elapsed = 0;

    progressIntervalRef.current = setInterval(() => {
      elapsed += updateInterval;
      setElapsedTime(elapsed);

      if (elapsed >= targetTime) {
        setProgress(99);
        clearInterval(progressIntervalRef.current);
      } else {
        setProgress((elapsed / targetTime) * 99);
      }
    }, updateInterval);
  };

  // ── Setup messages ──────────────────────────────────────────────────────────

  const startSetupMessages = () => {
    let currentIndex = 0;
    setSetupMessageIndex(0);

    messageIntervalRef.current = setInterval(() => {
      currentIndex = (currentIndex + 1) % SETUP_MESSAGES.length;
      setSetupMessageIndex(currentIndex);
    }, 10000);
  };

  // ── Open demo from complete state ───────────────────────────────────────────

  const handleOpenDemo = async () => {
    const currentDemoId = demoId || sessionStorage.getItem(DEMO_ID_STORAGE_KEY);
    const currentExpiresAt = expiresAt || sessionStorage.getItem(DEMO_EXPIRES_STORAGE_KEY);

    try {
      const response = await apiRequest(`${API_BASE_URL}/api/demos/${currentDemoId}/proxy-info`);
      const data = await response.json();
      if (onDemoReady) {
        onDemoReady({
          proxyBase: data.proxyBase,
          expiresAt: currentExpiresAt,
          demoId: currentDemoId
        });
      }
    } catch (err) {
      setError('Failed to connect to demo');
      setDemoState('error');
    }
  };

  // ── Button click ─────────────────────────────────────────────────────────────

  const isInProgress = demoState === 'polling-ip' || demoState === 'polling-ready';
  const isReady = demoState === 'complete';

  const handleButtonClick = () => {
    if (isReady) {
      handleOpenDemo();
      return;
    }

    if (!isOpen) {
      setIsOpen(true);
      setDemoState('email-entry');
      setEmailInput('');
      setEmailError('');
    } else {
      setIsOpen(false);
      setDemoState('closed');
    }
  };

  // ── Mobile guard ────────────────────────────────────────────────────────────

  if (isMobile) {
    return (
      <div className="live-demo-container">
        <button className="live-demo-button live-demo-button--disabled" disabled>
          <ExternalLink size={18} />
          Live Demo
          <span className="live-demo-mobile-hint">Web browser recommended</span>
        </button>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const buttonLabel = isInProgress ? 'Setting Up Demo' : isReady ? 'Running Demo' : 'Live Demo';

  return (
    <div className="live-demo-container">
      <button
        className={`live-demo-button${isInProgress ? ' live-demo-button--pending' : ''}`}
        onClick={handleButtonClick}
        disabled={isInProgress}
      >
        {isInProgress ? <Loader size={18} className="spin" /> : <ExternalLink size={18} />}
        {buttonLabel}
        {!isInProgress && !isReady && (isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />)}
      </button>

      {isOpen && (
        <>
          <div className="demo-backdrop" onClick={() => { setIsOpen(false); setDemoState('closed'); }} />
          <div className={`demo-dropdown${demoState === 'email-entry' ? ' demo-dropdown--compact' : ''}`}>

            {/* Code-entry gate (disabled — replaced by email capture below)
            {demoState === 'code-entry' && (
              <div className="demo-code-entry">
                <h3>Enter Access Code</h3>
                <input
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && verifyCode()}
                  placeholder="Enter code"
                  className="code-input"
                  autoFocus
                />
                {codeError && <p className="code-error">{codeError}</p>}
                <button className="verify-button" onClick={verifyCode}>
                  Verify & Continue
                </button>
                <p className="code-hint">Access can be requested by reaching out to me at - suraj.gupta@euris.com</p>
              </div>
            )}
            */}

            {demoState === 'email-entry' && (
              <div className="demo-email-entry">
                <h3>Mind if I follow up and hear what you think?</h3>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && submitEmail()}
                  placeholder="you@example.com"
                  className="email-input"
                  autoFocus
                />
                {emailError && <p className="email-error">{emailError}</p>}
                <button className="verify-button" onClick={submitEmail}>
                  Continue
                </button>
                <button className="skip-button" onClick={skipEmail}>
                  Skip it, straight to the demo
                </button>
              </div>
            )}

            {demoState === 'checking' && (
              <div className="demo-status">
                <Loader className="spin" size={24} />
                <p>Checking demo availability...</p>
              </div>
            )}

            {demoState === 'ready' && (
              <div className="demo-ready">
                <div className="demo-health">
                  <CheckCircle size={20} color="#10b981" />
                  <span>Demo is ready</span>
                </div>
                <div className="demo-health-details">
                  <div className="health-item">
                    <span>Status:</span>
                    <span className="health-value">{healthData.status}</span>
                  </div>
                  <div className="health-item">
                    <span>Database:</span>
                    <span className="health-value">{healthData.database}</span>
                  </div>
                  <div className="health-item">
                    <span>Active Demos:</span>
                    <span className="health-value">{healthData.activeDemos}</span>
                  </div>
                </div>
                <button className="launch-button" onClick={launchDemo}>
                  Launch Demo
                </button>
              </div>
            )}

            {demoState === 'error' && (
              <div className="demo-error">
                <XCircle size={48} color="#ef4444" />
                <h3 className="error-title">Demo Failed</h3>
                <p className="error-message">{error || 'Demo unavailable'}</p>
                <button className="retry-button" onClick={checkHealth}>
                  Try Again
                </button>
              </div>
            )}

          </div>
        </>
      )}
    </div>
  );
}

export default LiveDemo;
